import { Entity } from "src/app/core/data/schema/entity";
import { DateTime } from 'luxon';
import { CbsEvent, RegistrationType } from "./event";
import { TransactionStatus } from "../ecommerce/transaction";
import { ServiceHelper } from "src/app/core/helper/services.helper";
import { CommunityUser } from "../community-user";
import { PageState } from "src/app/core/base-page";

export enum EventRegistrationStatus {
    Pending = 1,
    Registered = 2,
    Canceled = 3,
    Yes = 4,
    No = 5,
    Maybe = 6,
    Refunded = 7
}

export class EventRegistrationRequest {
    eventTicketTypeId: number;
    eventId: number;
    quantity = 1;
    eventInstanceId?: number; // should be null for bundle event types
    paid: number;
    userId: number;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    user: CommunityUser;

    constructor(init: Partial<EventRegistrationRequest>) {
        Object.assign(this, init);
    }
}

export class EventRegistration extends Entity {

    protected static className = 'EventRegistration';
    protected static idField = 'eventRegistrationId';

    eventRegistrationId: number;
    reservationGuid: string;
    userId: number;
    eventId: number;
    eventInstanceId: number;
    transactionDetailId: number;
    transactionId: number;
    status: EventRegistrationStatus;
    checkedIn: boolean;
    paid: number;
    rating: number;
    review: string;
    txStatus: TransactionStatus;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    ticketTypeName: string;
    eventTicketTypeId: number;
    surveyResponseId: number;

    registrationDate: string;
    registerDateObj: DateTime

    startDate: string;
    startDateObj: DateTime;

    // mapped in API
    createdByName: string;
    userName: string;
    userEmail: string;

    // need these for pending time check
    created: string;
    createdObj: DateTime;
    get isPaid() { return this.txStatus == TransactionStatus.Approved }

    txState: string;

    // Event details come down with registration, and we use them
    // to instantiate an event object that we use to display card
    regEvent: CbsEvent;

    statusString: string;

    collectPayment: boolean = false;

    // assigned when registering existing user so it can be used on checkout button page as well
    user: CommunityUser;

    constructor(reg?: Partial<EventRegistration>) {
        super();
        Object.assign(this, reg);

        // only construct the reg event object if we actually got event fields on passed in object
        if (this['title'] != null && this['startDate'] != null) {
            this.regEvent = new CbsEvent(this);
        }

        // set the instance id right away if we have on:
        // if ( this.eventInstanceId > 0 && this.regEvent != null ) { this.regEvent.instanceId = this.eventInstanceId; }

        this.registerDateObj = DateTime.fromISO(this.registrationDate, { zone: 'utc' });
        this.createdObj = DateTime.fromISO(this.created, { zone: 'utc' });
        this.startDateObj = DateTime.fromISO(this.startDate, { zone: 'utc' });
        this.startDateObj.setZone('local');

        switch (this.txStatus) {
            case TransactionStatus.Pending: this.txState = 'Pending'; break;
            case TransactionStatus.Approved: this.txState = 'Paid'; break;
            case TransactionStatus.Refunded: this.txState = 'Refunded'; break;
            default: this.txState = 'Cancelled';
        }
    }

    override async save(busyState?: PageState, controller?: string | undefined, endpoint?: string, hideLoad?: boolean): Promise<boolean> {
        return super.save(busyState, controller, endpoint, hideLoad);
    }

    extraInit(event: CbsEvent) {
        if (event.registrationType == RegistrationType.RequireRegistration) {
            if (!(this.txStatus == 1 || this.txStatus == 2)) {
                this.statusString = TransactionStatus[this.txStatus];
                return;
            }
        }

        this.statusString = EventRegistrationStatus[this.status];
    }

    public static async fetchEventRegitrations(eventId: number) {
        var regs = await EventRegistration.fetchArray(EventRegistration, 'eventRegistrations', `forEvent/${eventId}`) as EventRegistration[];
        regs = regs.map(m => new EventRegistration(m));
        return regs;
    }

    public static async fetchEventRegitrationsForInstance(eventInstanceId: number) {
        var regs = await EventRegistration.fetchArray(EventRegistration, 'eventRegistrations', `forEventInstance/${eventInstanceId}`) as EventRegistration[];
        regs = regs.map(m => new EventRegistration(m));
        return regs;
    }

    public async checkin(checkin: boolean = true) {
        try {
            // Not Change as the function adds an `ing` to the word
            await Entity.factory.put('Chang', this, 'eventRegistrations', `checkin/${this.eventRegistrationId}/${checkin}`);
        } catch (e) {
            console.error('Error checking in');
            ServiceHelper.displayAlert('Error', 'An error occurred checking in user: ' + e);
            return !checkin;
        }
        return checkin;
    }

    public static async registerSpots(request: EventRegistrationRequest) {
        const registrations = await Entity.factory.httpService.post(request,
            `EventRegistrations/register`, 'json') as EventRegistration[];
        return registrations.map(m => new EventRegistration(m));
    }

    public static async cancelReservations(reservationGUIDs: string[]) {
        // ensure guids are unique
        reservationGUIDs = reservationGUIDs.filter((v, i, a) => a.indexOf(v) === i);

        if (reservationGUIDs.length === 0) {
            return;
        }

        await Entity.factory.httpService.post(reservationGUIDs, `EventRegistrations/cancel`, 'json');
    }
}
